import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DecimalPipe } from '@angular/common';

import { BlastDrillingData, DrillingSession, ShiftConfig } from 'src/app/shared/models/drilling.models';
import { DEFAULT_SHIFT_CONFIG } from 'src/app/services/drill-activity.service';
import { DrillKpiSummaryComponent } from './drill-kpi-summary/drill-kpi-summary.component';
import { BlastDrillingDeleteConfirmDialogComponent } from './delete-confirm-dialog.component';

export interface RigGroup {
  equipmentId: number;
  equipmentLabel: string;
  sessions: DrillingSession[];
}

@Component({
  selector: 'blast-drilling-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDialogModule,
    MatChipsModule,
    MatTooltipModule,
    DrillKpiSummaryComponent,
   
  ],
  templateUrl: './blast-drilling-panel.component.html',
  styleUrls: ['./blast-drilling-panel.component.scss'],
})
export class BlastDrillingPanelComponent {
  private readonly dialog = inject(MatDialog);

  // ---- Inputs ----------------------------------------------------------------

  readonly blast = input.required<BlastDrillingData>();
  readonly shiftConfig = input<ShiftConfig>(DEFAULT_SHIFT_CONFIG);

  // ---- Outputs ---------------------------------------------------------------

  readonly addSession = output<void>();
  readonly editSession = output<DrillingSession>();
  readonly deleteSession = output<DrillingSession>();

  // ---- Table sort state ------------------------------------------------------

  protected readonly sortActive = signal<string>('');
  protected readonly sortDirection = signal<SortDirection>('');

  protected readonly displayedColumns = [
    'index', 'rig', 'operator', 'shift', 'date', 'shiftDuration',
    'engineHours', 'pauseHours', 'drilledMeters', 'actions',
  ] as const;

  // ---- Computed data ---------------------------------------------------------

  protected readonly hasSessions = computed(() => this.blast().sessions.length > 0);

  protected readonly sortedSessions = computed(() => {
    const sessions = [...this.blast().sessions];
    const active = this.sortActive();
    const dir = this.sortDirection();

    if (!dir) {
      // Default compound sort: date → shiftIndex → equipmentLabel
      return sessions.sort(
        (a, b) =>
          a.date.localeCompare(b.date) ||
          a.shiftIndex - b.shiftIndex ||
          a.equipmentLabel.localeCompare(b.equipmentLabel),
      );
    }

    const mult = dir === 'asc' ? 1 : -1;
    return sessions.sort((a, b) => {
      switch (active) {
        case 'rig':          return mult * a.equipmentLabel.localeCompare(b.equipmentLabel);
        case 'operator':     return mult * a.operatorLabel.localeCompare(b.operatorLabel);
        case 'shift':        return mult * (a.shiftIndex - b.shiftIndex);
        case 'shiftDuration': return mult * (a.shiftDurationHours - b.shiftDurationHours);
        case 'engineHours':  return mult * (a.engineHours - b.engineHours);
        case 'pauseHours':   return mult * (a.pauseHours - b.pauseHours);
        case 'drilledMeters': return mult * (a.drilledMeters - b.drilledMeters);
        default:             return 0;
      }
    });
  });

  protected readonly rigGroups = computed((): RigGroup[] => {
    const map = new Map<number, RigGroup>();
    for (const s of this.blast().sessions) {
      if (!map.has(s.equipmentId)) {
        map.set(s.equipmentId, {
          equipmentId: s.equipmentId,
          equipmentLabel: s.equipmentLabel,
          sessions: [],
        });
      }
      map.get(s.equipmentId)!.sessions.push(s);
    }
    return [...map.values()].sort((a, b) =>
      a.equipmentLabel.localeCompare(b.equipmentLabel),
    );
  });

  // ---- Footer totals ---------------------------------------------------------

  protected readonly footerTotalShiftDuration = computed(() =>
    this.blast().sessions.reduce((sum, s) => sum + s.shiftDurationHours, 0),
  );

  protected readonly footerTotalEngineHours = computed(() =>
    this.blast().sessions.reduce((sum, s) => sum + s.engineHours, 0),
  );

  protected readonly footerTotalPauseHours = computed(() =>
    this.blast().sessions.reduce((sum, s) => sum + s.pauseHours, 0),
  );

  protected readonly footerTotalDrilled = computed(() =>
    this.blast().sessions.reduce((sum, s) => sum + s.drilledMeters, 0),
  );

  // ---- Event handlers --------------------------------------------------------

  protected onSortChange(sort: Sort): void {
    this.sortActive.set(sort.active);
    this.sortDirection.set(sort.direction);
  }

  protected onAddSession(): void {
    this.addSession.emit();
  }

  protected onEditSession(session: DrillingSession): void {
    this.editSession.emit(session);
  }

  protected onDeleteSession(session: DrillingSession): void {
    const ref = this.dialog.open(BlastDrillingDeleteConfirmDialogComponent);
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteSession.emit(session);
      }
    });
  }

  // ---- Helpers ---------------------------------------------------------------

  protected shiftLabel(shiftIndex: number): string {
    const labels = this.shiftConfig().shiftLabels;
    return labels[shiftIndex] ?? `Shift ${shiftIndex + 1}`;
  }

  protected shiftCssClass(shiftIndex: number): string {
    return `shift-${this.shiftLabel(shiftIndex).toLowerCase()}`;
  }

  protected formatPause(hours: number): string {
    const h = Math.floor(hours);
    const min = Math.round((hours - h) * 60);
    if (h === 0) return `${min} min`;
    if (min === 0) return `${h}h`;
    return `${h}h ${min}min`;
  }

  protected sessionBarWidthPx(drilledMeters: number): string {
    const target = this.blast().targetMeters;
    if (target <= 0) return '0px';
    return `${Math.min(80, Math.round((drilledMeters / target) * 80))}px`;
  }
}
